import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FaSync } from "react-icons/fa";
import {
  getAllUsersWithClub,
  deleteUser,
  updateUser,
} from "../../../services/userServices";
import { getClubById } from "../../../services/clubServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import useScrollToTop from "../../../hooks/useScollToTop";
import UserModal from "./UserModal";

import { UsersFilters } from "./components/UsersFilters";
import { UsersTable } from "./components/UsersTable";
import { UsersCards } from "./components/UsersCards";

const GRADIENT =
  "bg-gradient-to-r from-[#5A8DEE] via-[#8753FF] to-[#FF6A88] text-white";

export const ROLES = [
  { id: 1, label: "Administrateur", badge: "red" },
  { id: 2, label: "Démo", badge: "green" },
  { id: 3, label: "Présence", badge: "blue" },
  { id: 4, label: "Présence Pro", badge: "indigo" },
  { id: 5, label: "Présence Ultim", badge: "violet" },
  { id: 6, label: "Enseignant", badge: "orange" },
  { id: 7, label: "Présence Free", badge: "emerald" },
];

const roleById = new Map(ROLES.map((r) => [r.id, r]));

function fmtDate(d) {
  try {
    return new Date(d).toLocaleDateString("fr-FR");
  } catch {
    return "—";
  }
}

export default function Users() {
  useScrollToTop();
  const { addNotification } = useNotification();

  const [users, setUsers] = useState([]);
  const [viewMode, setViewMode] = useState("table");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const [isModalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [clubNames, setClubNames] = useState(new Map());

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllUsersWithClub();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      addNotification(
        "Erreur lors de la récupération des utilisateurs.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Précharge les noms de clubs pour tous les utilisateurs ayant un club_id
  useEffect(() => {
    const needed = new Set();

    for (const u of users) {
      const cid = u?.Club?.id ?? u?.club_id;
      if (cid && !clubNames.has(cid)) {
        needed.add(cid);
      }
    }

    if (needed.size === 0) return;

    let isCancelled = false;

    (async () => {
      try {
        const entries = await Promise.all(
          Array.from(needed).map(async (cid) => {
            try {
              const club = await getClubById(cid);
              return [cid, club?.nom_club || `Club #${cid}`];
            } catch {
              return [cid, `Club #${cid}`];
            }
          }),
        );

        if (!isCancelled) {
          setClubNames((prev) => {
            const next = new Map(prev);
            for (const [cid, name] of entries) {
              next.set(cid, name);
            }
            return next;
          });
        }
      } catch (e) {
        console.warn("Préchargement des clubs échoué", e);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [users, clubNames]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    let out = users.filter((u) => {
      const clubId = u?.Club?.id ?? u?.club_id;
      const clubLabel =
        u?.Club?.nom_club || (clubId ? clubNames.get(clubId) : "") || "";

      const matchesTerm =
        !term ||
        `${u.nom || ""} ${u.prenom || ""}`.toLowerCase().includes(term) ||
        (u.email || "").toLowerCase().includes(term) ||
        clubLabel.toLowerCase().includes(term);

      const matchesRole = selectedRole
        ? String(u.role_id) === String(selectedRole)
        : true;

      return matchesTerm && matchesRole;
    });

    out.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;

      if (sortBy === "date") {
        return (new Date(a.createdOn) - new Date(b.createdOn)) * dir;
      }

      return (
        `${a.nom || ""} ${a.prenom || ""}`.localeCompare(
          `${b.nom || ""} ${b.prenom || ""}`,
          "fr",
        ) * dir
      );
    });

    return out;
  }, [users, searchTerm, selectedRole, sortBy, sortDir, clubNames]);

  const handleDelete = async (userId) => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;

    try {
      await deleteUser(userId);
      addNotification("Utilisateur supprimé.", "success");
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch {
      addNotification("Erreur lors de la suppression.", "error");
    }
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const closeEdit = () => {
    setEditingUser(null);
    setModalOpen(false);
  };

  const handleSubmitUser = async (payload) => {
    try {
      await updateUser(editingUser.id, payload);
      addNotification("Utilisateur mis à jour.", "success");
      await fetchUsers();
      closeEdit();
    } catch {
      addNotification("Erreur lors de la mise à jour.", "error");
    }
  };

  const handleInlineRole = async (user, newRoleId) => {
    const nextRoleId = parseInt(newRoleId, 10);
    const oldRoleId = user.role_id;

    if (!Number.isInteger(nextRoleId)) {
      addNotification("Rôle invalide.", "error");
      return;
    }

    if (Number(oldRoleId) === nextRoleId) {
      return;
    }

    const clubId = user?.Club?.id ?? user?.club_id ?? null;

    const payload = {
      nom: user.nom || "",
      prenom: user.prenom || "",
      email: user.email || "",
      role_id: nextRoleId,
      club_id: clubId ? parseInt(clubId, 10) : null,
    };

    setUsers((prev) =>
      prev.map((u) => (u.id === user.id ? { ...u, role_id: nextRoleId } : u)),
    );

    try {
      await updateUser(user.id, payload);
      addNotification("Rôle mis à jour.", "success");
    } catch (e) {
      console.error("Erreur update rôle inline :", e?.response?.data || e);

      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, role_id: oldRoleId } : u)),
      );

      addNotification("Échec de mise à jour du rôle.", "error");
    }
  };

  const kpis = useMemo(() => {
    const total = filtered.length;
    const byRole = ROLES.reduce((acc, r) => ({ ...acc, [r.id]: 0 }), {});

    for (const u of filtered) {
      if (byRole[u.role_id] !== undefined) {
        byRole[u.role_id]++;
      }
    }

    return { total, byRole };
  }, [filtered]);

  return (
    <div className="space-y-6 mt-[100px]">
      <div className={`rounded-2xl p-6 shadow-sm ${GRADIENT}`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">
              Utilisateurs de l’application
            </h1>
            <p className="text-sm opacity-90 mt-1">
              Rôles, clubs rattachés et gestion rapide.
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={fetchUsers}
              className="bg-white/15 hover:bg-white/25 text-white px-4 py-2 rounded-xl inline-flex items-center gap-2"
            >
              <FaSync /> Rafraîchir
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3 mt-4">
          <KpiCard label="Total" value={kpis.total} />
          {ROLES.map((r) => (
            <KpiCard
              key={r.id}
              label={r.label}
              value={kpis.byRole[r.id] || 0}
            />
          ))}
        </div>
      </div>

      <div className="rounded-2xl p-4 bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-800">
        <UsersFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
          roles={ROLES}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortDir={sortDir}
          setSortDir={setSortDir}
          viewMode={viewMode}
          setViewMode={setViewMode}
          totalCount={filtered.length}
        />
      </div>

      <div className="rounded-2xl overflow-hidden bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-800">
        {loading ? (
          <SkeletonTable />
        ) : filtered.length === 0 ? (
          <EmptyList />
        ) : viewMode === "table" ? (
          <UsersTable
            data={filtered}
            onEdit={openEdit}
            onDelete={handleDelete}
            onInlineRole={handleInlineRole}
            roleById={roleById}
            fmtDate={fmtDate}
            roles={ROLES}
            clubNames={clubNames}
          />
        ) : (
          <UsersCards
            data={filtered}
            onEdit={openEdit}
            onDelete={handleDelete}
            onInlineRole={handleInlineRole}
            roleById={roleById}
            fmtDate={fmtDate}
            roles={ROLES}
            clubNames={clubNames}
          />
        )}
      </div>

      {isModalOpen && (
        <UserModal
          isOpen={isModalOpen}
          onClose={closeEdit}
          onSubmit={handleSubmitUser}
          user={editingUser}
        />
      )}
    </div>
  );
}

function KpiCard({ label, value, hint }) {
  return (
    <div className="rounded-2xl p-4 bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 shadow-sm">
      <div className="text-xs text-slate-600 dark:text-slate-400">{label}</div>
      <div className="text-2xl font-semibold mt-1 text-slate-900 dark:text-slate-50 tabular-nums">
        {value}
      </div>
      {hint ? (
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

function SkeletonTable() {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-14 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse"
        />
      ))}
    </div>
  );
}

function EmptyList() {
  return (
    <div className="p-10 text-center">
      <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
        📭
      </div>
      <div className="font-medium">
        Aucun utilisateur ne correspond à vos filtres.
      </div>
      <div className="text-sm text-slate-500 mt-1">
        Essayez d’élargir votre recherche.
      </div>
    </div>
  );
}
